import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import CustomTextField from '../../CustomTextField';
import CustomDropDown from '../../CustomDropDown';
import GroupedDropDown from '../../CustomComponents/GroupedDropDown';
import MultiSelectDropDown from '../../CustomComponents/MultiSelectDropDown';
import Loader from '../../Loader';
import { headingBlue, red } from '../../../assets/colors';
import { buttonStyle } from '../../../utils/commonCSS';
import { WORKerTypesList } from '../../../utils/constants';
import HttpService from '../../../backend/requestApis';
import { getVolunteeringDomainsHierarchy, createTrainingProgram } from '../../../backend/backendApis';
import { useNavigate } from 'react-router-dom';

const AddProgramDialog = (props) => {
  const navigate = useNavigate();
  const [programName, setProgramName] = useState('');
  const [programNameError, setProgramNameError] = useState('');
  const [programMode, setProgramMode] = useState('Program Mode *');
  const [programModeError, setProgramModeError] = useState('');
  const [volunteerDomain, setVolunteerDomain] = useState('Volunteer Domain *');
  const [volunteerDomainError, setVolunteerDomainError] = useState('');
  const [applicableFor, setApplicableFor] = useState([]);
  const [applicableForError, setApplicableForError] = useState('');
  
  const [isLoader, setLoader] = useState(false);
  const [apiError, setApiError] = useState('');

  const modeList = [{name: 'Online'}, {name: 'Offline'}, {name: 'Hybrid'}];
  const [domainList, setDomainList] = useState([]);
  const [rawDomains, setRawDomains] = useState([]);
  const applicableList = WORKerTypesList.map(type => ({ name: type.label }));

  var titleStyle = {
    backgroundColor: headingBlue, 
    color: 'white',
    fontSize: '1rem',
    display: "flex",
    alignItems: 'center',
    padding: "0.4rem 0rem 0.4rem 1rem" 
  };

  var errorStyleText = {
    color: red,
    padding: "0.2rem 0rem 0rem 0rem",
    fontSize: 11,
  };

  useEffect(() => {
    setProgramName('');
    setProgramNameError('');
    setProgramMode('Program Mode *');
    setProgramModeError('');
    setVolunteerDomain('Volunteer Domain *');
    setVolunteerDomainError('');
    setApplicableFor([]);
    setApplicableForError('');
    setApiError('');
  }, [props.dialogStatus]);

  useEffect(() => {
    try {
      if (props.dialogStatus) {
        setLoader(true);
        let options = {
          url: getVolunteeringDomainsHierarchy,
          method: 'GET',
          headers: {
            Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
          }
        };
        HttpService(options)
          .then((resp) => {
            let fetchedDomains = resp?.data?.data || [];
            setRawDomains(fetchedDomains);
            let formattedList = fetchedDomains.map(domain => {
              return {
                parentName: domain.areaName || domain.domainName || 'Unknown',
                children: Array.isArray(domain.children) 
                  ? domain.children.map(child => ({
                      _id: child._id,
                      name: child.areaName || child.domainName || 'Unknown'
                    }))
                  : []
              };
            });
            setDomainList(formattedList);
            setLoader(false);
          })
          .catch((err) => {
            if (err?.status === 403) {
              localStorage.clear();
              setLoader(false);
              navigate('/Login');
            } else {
              console.log('Error fetching volunteering domains: ', err);
              setLoader(false);
            }
          });
      }
    } catch (err) {
      console.log('Error in fetching volunteering domains: ', err);
      setLoader(false);
    }
  }, [props.dialogStatus]);

  const handleAddProgram = () => {
    let isValid = true;
    if (!programName) { setProgramNameError('Program Name is required'); isValid = false; } else { setProgramNameError(''); }
    if (programMode === 'Program Mode *') { setProgramModeError('Program Mode is required'); isValid = false; } else { setProgramModeError(''); }
    if (volunteerDomain === 'Volunteer Domain *') { setVolunteerDomainError('Volunteer Domain is required'); isValid = false; } else { setVolunteerDomainError(''); }
    if (applicableFor.length === 0) { setApplicableForError('Applicable For is required'); isValid = false; } else { setApplicableForError(''); }

    if (isValid) {
      setLoader(true);
      
      let selectedDomainId = volunteerDomain;

      let mappedApplicableFor = applicableFor.map(label => {
        let match = WORKerTypesList.find(type => type.label === label);
        return match ? match.value : label;
      });

      let options = {
        url: createTrainingProgram,
        method: 'POST',
        payload: {
            programName: programName,
            programMode: programMode,
            programVolunteerDomain: selectedDomainId,
            programApplicableFor: mappedApplicableFor
        },
        headers: {
          headers: {
            Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : ''
          }
        }
      };

      HttpService(options).then((resp) => {
        setLoader(false);
        props.updateFunc(false, true); // Close Dialog
      }).catch((err) => {
        setLoader(false);
        if (err?.status === 403) {
          localStorage.clear();
          navigate('/Login');
        } else {
          console.log('Error creating program: ', err);
          setApiError('Error creating program. Please try again.');
        }
      });
    }
  };

  return (
    <Dialog maxWidth='md' fullWidth open={props.dialogStatus} onClose={() => props.updateFunc(false)}>
        <DialogTitle style={titleStyle}>
            Add New Program
        </DialogTitle>
        <DialogContent>
            <Box style={{padding: '1.5rem 0rem 0rem 0rem'}}>
                {isLoader && <Loader />}
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <CustomTextField placeHolder='Program Name *' value={programName} setFunc={(value) => setProgramName(value)} width="15rem" />
                        <Typography style={{...errorStyleText}}>{programNameError}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <CustomDropDown placeHolder='Program Mode *' list={modeList} value={programMode} setFunc={(value) => setProgramMode(value)} />
                        <Typography style={{...errorStyleText}}>{programModeError}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <GroupedDropDown placeHolder='Volunteer Domain *' list={domainList} value={volunteerDomain} setFunc={(value) => setVolunteerDomain(value)} />
                        <Typography style={{...errorStyleText}}>{volunteerDomainError}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <MultiSelectDropDown placeHolder='Applicable For *' list={applicableList} value={applicableFor} setFunc={(value) => setApplicableFor(value)} />
                        <Typography style={{...errorStyleText}}>{applicableForError}</Typography>
                    </Grid>
                </Grid>
                
                <Box style={{padding: '1rem 0rem 0rem 0rem', display: 'flex', justifyContent: 'center'}}>
                    <Button style={{...buttonStyle, padding: '0.2rem 0.4rem', backgroundColor: red}}
                      onClick={() => props.updateFunc(false)}
                    >Cancel</Button>
                    <Button style={{...buttonStyle, padding: '0.2rem 0.4rem', marginLeft: '0.5rem'}}
                      onClick={() => handleAddProgram()}
                    >Add</Button>
                </Box>

                {apiError && (
                  <Box style={{display: 'flex', justifyContent: 'center', padding: '1rem 0rem 0rem 0rem'}}>
                    <Typography style={{...errorStyleText, fontSize: '0.85rem'}}>{apiError}</Typography>
                  </Box>
                )}
            </Box>
        </DialogContent>
    </Dialog>
  );
};

export default AddProgramDialog;